import { useUpdateEffect } from 'react-use';

import { ICD9Procedure } from '@dreryk/egabinet-icd-dictionary-frontend-api';

import { Translate, useFormController } from '@dreryk/react-common';
import { Autosuggest, FormLabel, InfoTooltip, StyledFlex } from '@dreryk/react-components';

import { referralIcd9ProcedureTranslationKeys, referralTranslationKeys } from '../../translations';

import { createIcd9ProcedureSearchCreator } from './Icd9ProcedureField.hooks';

type Icd9ProcedureFieldProps = {
   readonly addIcd9Procedure: (icd9Procedure: ICD9Procedure) => void;
   readonly dictionaryValidOn: Date;
   readonly invalid?: boolean;
   readonly validate: () => void;
};
type Icd9ProcedureFormBean = {
   icd9Procedure: ICD9Procedure | null;
};

const initialValues: Icd9ProcedureFormBean = {
   icd9Procedure: null,
};

const labelExtractor = (item: ICD9Procedure): string => item.name;

const itemRenderer = (icd9ProcedureType: ICD9Procedure): JSX.Element => <div>{`${icd9ProcedureType.name}`}</div>;

export const Icd9ProcedureField = (props: Icd9ProcedureFieldProps): JSX.Element => {
   const { register, trigger, setValue } = useFormController<Icd9ProcedureFormBean>(initialValues);

   useUpdateEffect(() => {
      trigger();
   }, [props.invalid]);

   const onDiagnosticExaminationTypeSelection = (icd9Procedure: ICD9Procedure | null): void => {
      if (icd9Procedure) {
         props.addIcd9Procedure(icd9Procedure);
         setTimeout(() => {
            setValue('icd9Procedure', null);
         }, 0);
      }
   };

   return (
      <Autosuggest<ICD9Procedure>
         label={
            <FormLabel
               label={
                  <StyledFlex direction={'row'} alignItems={'center'} flexWrap={'nowrap'} gap={'4px'}>
                     <FormLabel
                        label={referralIcd9ProcedureTranslationKeys.examinationType}
                        testId='icd9Procedure'
                        required
                     />
                     <InfoTooltip
                        testId={'icd9Procedure'}
                        content={<Translate>{referralIcd9ProcedureTranslationKeys.examinationTypeTooltip}</Translate>}
                     />
                  </StyledFlex>
               }
               testId='icd9Procedure'
            />
         }
         placeholder={referralTranslationKeys.insertDiagnosticExamination}
         minChars={3}
         labelExtractor={labelExtractor}
         itemRenderer={itemRenderer}
         searchCreator={createIcd9ProcedureSearchCreator(props.dictionaryValidOn)}
         onSelectionChange={onDiagnosticExaminationTypeSelection}
         keyProperty={'code'}
         controller={register('icd9Procedure', {
            validate: () => {
               props.validate();
               if (props.invalid) {
                  return 'common:validation.required';
               }
               return undefined;
            },
         })}
         testId='icd9Procedure'
      />
   );
};
